import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as svc from '../../services/settingsService';
import type { Branch, ServiceCategory, Service, BranchSettings } from '../../types';

interface SettingsState {
  branch:     Branch | null;
  categories: ServiceCategory[];
  services:   Service[];
  settings:   BranchSettings | null;
  loading:    boolean;
  error:      string | null;
}

const initialState: SettingsState = {
  branch: null, categories: [], services: [], settings: null,
  loading: false, error: null,
};

type ApiError = { response?: { data?: { message?: string } } };
const msg = (e: unknown) => (e as ApiError).response?.data?.message ?? 'Request failed';

// ─── Thunks ───────────────────────────────────────────────────────────────────
export const fetchBranch = createAsyncThunk('settings/fetchBranch', async (_, { rejectWithValue }) => {
  try { return (await svc.fetchBranch()).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const updateBranch = createAsyncThunk('settings/updateBranch', async (data: Partial<Branch>, { rejectWithValue }) => {
  try { return (await svc.updateBranch(data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const addHoliday = createAsyncThunk('settings/addHoliday', async ({ date, label }: { date: string; label: string }, { rejectWithValue }) => {
  try { return (await svc.addHoliday(date, label)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const deleteHoliday = createAsyncThunk('settings/deleteHoliday', async (holidayId: string, { rejectWithValue }) => {
  try { return (await svc.deleteHoliday(holidayId)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const uploadBranchLogo = createAsyncThunk('settings/uploadBranchLogo', async (file: File, { rejectWithValue }) => {
  try { return (await svc.uploadBranchLogo(file)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});

export const fetchCategories = createAsyncThunk('settings/fetchCategories', async (_, { rejectWithValue }) => {
  try { return (await svc.fetchCategories()).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const createCategory = createAsyncThunk('settings/createCategory', async (data: { name: string; displayName: string; parentCategory?: string | null; genders: ServiceCategory['genders']; sortOrder?: number }, { rejectWithValue }) => {
  try { return (await svc.createCategory(data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const updateCategory = createAsyncThunk('settings/updateCategory', async ({ id, data }: { id: string; data: Partial<ServiceCategory> }, { rejectWithValue }) => {
  try { return (await svc.updateCategory(id, data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const deleteCategory = createAsyncThunk('settings/deleteCategory', async (id: string, { rejectWithValue }) => {
  try { await svc.deleteCategory(id); return id; } catch (e) { return rejectWithValue(msg(e)); }
});

export const fetchServices = createAsyncThunk('settings/fetchServices', async (params: svc.ServiceFilters | undefined, { rejectWithValue }) => {
  try { return (await svc.fetchServices(params)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const createService = createAsyncThunk('settings/createService', async (data: Omit<Service, '_id' | 'branch' | 'images'>, { rejectWithValue }) => {
  try { return (await svc.createService(data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const updateService = createAsyncThunk('settings/updateService', async ({ id, data }: { id: string; data: Partial<Service> }, { rejectWithValue }) => {
  try { return (await svc.updateService(id, data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const deleteService = createAsyncThunk('settings/deleteService', async (id: string, { rejectWithValue }) => {
  try { await svc.deleteService(id); return id; } catch (e) { return rejectWithValue(msg(e)); }
});

export const fetchSettings = createAsyncThunk('settings/fetchSettings', async (_, { rejectWithValue }) => {
  try { return (await svc.fetchSettings()).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});
export const updateSettings = createAsyncThunk('settings/updateSettings', async (data: Partial<BranchSettings>, { rejectWithValue }) => {
  try { return (await svc.updateSettings(data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});

// ─── Slice ────────────────────────────────────────────────────────────────────
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    const pending  = (s: SettingsState) => { s.loading = true; s.error = null; };
    const rejected = (s: SettingsState, a: { payload: unknown }) => { s.loading = false; s.error = a.payload as string; };

    builder
      .addCase(fetchBranch.pending,    pending)
      .addCase(fetchBranch.fulfilled,  (s, a) => { s.loading = false; s.branch = a.payload; })
      .addCase(fetchBranch.rejected,   rejected)
      .addCase(updateBranch.fulfilled, (s, a) => { s.branch = a.payload; })
      .addCase(addHoliday.fulfilled,   (s, a) => { s.branch = a.payload; })
      .addCase(deleteHoliday.fulfilled,(s, a) => { s.branch = a.payload; })
      .addCase(uploadBranchLogo.fulfilled,(s, a) => { s.branch = a.payload; })

      .addCase(fetchCategories.pending,    pending)
      .addCase(fetchCategories.fulfilled,  (s, a) => { s.loading = false; s.categories = a.payload; })
      .addCase(fetchCategories.rejected,   rejected)
      .addCase(createCategory.fulfilled,   (s, a) => { s.categories.push(a.payload); })
      .addCase(updateCategory.fulfilled,   (s, a) => {
        const i = s.categories.findIndex(c => c._id === a.payload._id);
        if (i !== -1) s.categories[i] = a.payload;
      })
      .addCase(deleteCategory.fulfilled,   (s, a) => { s.categories = s.categories.filter(c => c._id !== a.payload); })

      .addCase(fetchServices.pending,    pending)
      .addCase(fetchServices.fulfilled,  (s, a) => { s.loading = false; s.services = a.payload; })
      .addCase(fetchServices.rejected,   rejected)
      .addCase(createService.fulfilled,  (s, a) => { s.services.push(a.payload); })
      .addCase(updateService.fulfilled,  (s, a) => {
        const i = s.services.findIndex(sv => sv._id === a.payload._id);
        if (i !== -1) s.services[i] = a.payload;
      })
      .addCase(deleteService.fulfilled,  (s, a) => { s.services = s.services.filter(sv => sv._id !== a.payload); })

      .addCase(fetchSettings.pending,    pending)
      .addCase(fetchSettings.fulfilled,  (s, a) => { s.loading = false; s.settings = a.payload; })
      .addCase(fetchSettings.rejected,   rejected)
      .addCase(updateSettings.fulfilled, (s, a) => { s.settings = a.payload; });
  },
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer;
